<script>
import { nanoid } from 'nanoid';
import FilterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  getOperators,
  getCustomAttributeType,
  getInputType,
  getDefaultConditions,
  getDefaultActions,
} from 'dashboard/helper/automationHelper';

export default {
  name: 'BranchItem',
  components: {
    FilterInputBox,
    AutomationActionInput,
    NextButton,
  },
  props: {
    branch: {
      type: Object,
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
    filterAttributes: {
      type: Array,
      default: () => [],
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    getConditionDropdownValues: {
      type: Function,
      default: () => () => [],
    },
    getActionDropdownValues: {
      type: Function,
      default: () => () => [],
    },
    allCustomAttributes: {
      type: Array,
      default: () => [],
    },
    automation: {
      type: Object,
      default: () => ({}),
    },
    automationTypes: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update', 'remove'],
  computed: {
    conditionsWithMetadata() {
      const conditions = Array.isArray(this.branch.conditions)
        ? this.branch.conditions
        : [];

      return conditions.map((condition, index) => {
        const dropdownValues = this.getConditionDropdownValues(
          condition.attribute_key
        );
        const inputType = getInputType(
          this.allCustomAttributes,
          this.automationTypes,
          this.automation,
          condition.attribute_key
        );

        return {
          ...condition,
          _index: index,
          _dropdownValues: dropdownValues,
          _operators: this.getOperators(condition.attribute_key),
          _customAttributeType: this.getCustomAttributeType(
            condition.attribute_key
          ),
          _inputType: inputType,
        };
      });
    },
  },
  methods: {
    getOperators(key) {
      return getOperators(
        this.allCustomAttributes,
        this.automationTypes,
        this.automation,
        'edit',
        key
      );
    },
    getCustomAttributeType(key) {
      return getCustomAttributeType(this.automationTypes, this.automation, key);
    },
    updateBranchName(event) {
      this.$emit('update', { ...this.branch, name: event.target.value });
    },
    appendCondition() {
      const currentConditions = Array.isArray(this.branch.conditions)
        ? this.branch.conditions
        : [];
      const defaultCondition = getDefaultConditions(this.automation.event_name);
      const newConditions = [...currentConditions, defaultCondition[0]];
      this.$emit('update', { ...this.branch, conditions: newConditions });
    },
    removeCondition(index) {
      const currentConditions = Array.isArray(this.branch.conditions)
        ? this.branch.conditions
        : [];
      if (currentConditions.length > 1) {
        const newConditions = currentConditions.filter((_, i) => i !== index);
        this.$emit('update', { ...this.branch, conditions: newConditions });
      }
    },
    resetCondition(index) {
      const currentConditions = Array.isArray(this.branch.conditions)
        ? this.branch.conditions
        : [];
      const defaultCondition = getDefaultConditions(this.automation.event_name);
      const newConditions = [...currentConditions];
      newConditions[index] = defaultCondition[0];
      this.$emit('update', { ...this.branch, conditions: newConditions });
    },
    appendAction() {
      const currentActions = Array.isArray(this.branch.actions)
        ? this.branch.actions
        : [];
      const defaultAction = getDefaultActions();
      const newActions = [...currentActions, defaultAction[0]];
      this.$emit('update', { ...this.branch, actions: newActions });
    },
    removeAction(index) {
      const currentActions = Array.isArray(this.branch.actions)
        ? this.branch.actions
        : [];
      if (currentActions.length > 1) {
        const newActions = currentActions.filter((_, i) => i !== index);
        this.$emit('update', { ...this.branch, actions: newActions });
      }
    },
    resetAction(index) {
      const currentActions = Array.isArray(this.branch.actions)
        ? this.branch.actions
        : [];
      const defaultAction = getDefaultActions();
      const newActions = [...currentActions];
      newActions[index] = defaultAction[0];
      this.$emit('update', { ...this.branch, actions: newActions });
    },
    addSubBranch() {
      const defaultCondition = getDefaultConditions(this.automation.event_name);
      const defaultAction = getDefaultActions();
      const newSubBranch = {
        id: nanoid(),
        name: `Sub-Rama ${(this.branch.branches || []).length + 1}`,
        conditions: defaultCondition,
        actions: defaultAction,
        branches: [],
      };
      const newBranches = [...(this.branch.branches || []), newSubBranch];
      this.$emit('update', { ...this.branch, branches: newBranches });
    },
    removeSubBranch(index) {
      const currentBranches = Array.isArray(this.branch.branches)
        ? this.branch.branches
        : [];
      const newBranches = currentBranches.filter((_, i) => i !== index);
      this.$emit('update', { ...this.branch, branches: newBranches });
    },
    updateSubBranch(index, updatedSubBranch) {
      const currentBranches = Array.isArray(this.branch.branches)
        ? this.branch.branches
        : [];
      const newBranches = [...currentBranches];
      newBranches[index] = updatedSubBranch;
      this.$emit('update', { ...this.branch, branches: newBranches });
    },
  },
};
</script>

<template>
  <div
    class="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
    :class="{ 'ml-6': depth > 0 }"
  >
    <!-- Nombre de la rama -->
    <div class="mb-3 flex items-center justify-between">
      <input
        :value="branch.name"
        type="text"
        class="flex-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900"
        :placeholder="$t('AUTOMATION.BRANCHES.BRANCH_NAME')"
        @input="updateBranchName"
      />
      <button
        class="ml-2 text-red-500 hover:text-red-700"
        @click="$emit('remove')"
      >
        <fluent-icon icon="delete" size="16" />
      </button>
    </div>

    <!-- Condiciones -->
    <section class="mb-3">
      <label
        class="mb-2 block text-sm font-medium text-slate-600 dark:text-slate-400"
      >
        {{ $t('AUTOMATION.BRANCHES.CONDITIONS') }}
      </label>
      <div class="space-y-2">
        <FilterInputBox
          v-for="condMeta in conditionsWithMetadata"
          :key="`${branch.id || 'branch'}-condition-${condMeta._index}-${condMeta.attribute_key}-${condMeta.filter_operator}`"
          :model-value="condMeta"
          :filter-attributes="filterAttributes"
          :input-type="condMeta._inputType"
          :dropdown-values="condMeta._dropdownValues"
          :operators="condMeta._operators"
          :custom-attribute-type="condMeta._customAttributeType"
          show-query-operator
          show-user-input
          :all-custom-attributes="allCustomAttributes"
          @update:model-value="
            val => {
              const currentConditions = Array.isArray(branch.conditions)
                ? branch.conditions
                : [];
              const newConditions = [...currentConditions];
              newConditions[condMeta._index] = val;
              $emit('update', { ...branch, conditions: newConditions });
            }
          "
          @reset-filter="resetCondition(condMeta._index)"
          @remove-filter="removeCondition(condMeta._index)"
        />
        <NextButton
          icon="i-lucide-plus"
          :label="$t('AUTOMATION.BRANCHES.ADD_CONDITION')"
          variant="clear"
          color-scheme="primary"
          size="small"
          @click="appendCondition"
        />
      </div>
    </section>

    <!-- Acciones -->
    <section class="mb-3">
      <label
        class="mb-2 block text-sm font-medium text-slate-600 dark:text-slate-400"
      >
        {{ $t('AUTOMATION.BRANCHES.ACTIONS') }}
      </label>
      <div class="space-y-2">
        <AutomationActionInput
          v-for="(action, actIndex) in branch.actions || []"
          :key="`action-${actIndex}-${action.action_name}`"
          :model-value="action"
          :action-types="actionTypes"
          :dropdown-values="getActionDropdownValues(action.action_name)"
          :all-custom-attributes="allCustomAttributes"
          :automation-types="automationTypes"
          show-action-input
          @update:model-value="
            val => {
              const currentActions = Array.isArray(branch.actions)
                ? branch.actions
                : [];
              const newActions = [...currentActions];
              newActions[actIndex] = val;
              $emit('update', { ...branch, actions: newActions });
            }
          "
          @reset-action="resetAction(actIndex)"
          @remove-action="removeAction(actIndex)"
        />
        <NextButton
          icon="i-lucide-plus"
          :label="$t('AUTOMATION.BRANCHES.ADD_ACTION')"
          variant="clear"
          color-scheme="primary"
          size="small"
          @click="appendAction"
        />
      </div>
    </section>

    <!-- Sub-Ramas (Recursivo) -->
    <section
      v-if="
        branch.branches &&
        Array.isArray(branch.branches) &&
        branch.branches.length > 0
      "
      class="mt-4 border-t border-slate-200 pt-4 dark:border-slate-700"
    >
      <label
        class="mb-2 block text-sm font-medium text-slate-600 dark:text-slate-400"
      >
        {{ $t('AUTOMATION.BRANCHES.SUB_BRANCHES') }}
      </label>
      <div
        class="space-y-3 border-l-2 border-slate-300 pl-4 dark:border-slate-600"
      >
        <!-- Componente recursivo -->
        <BranchItem
          v-for="(subBranch, subIndex) in branch.branches"
          :key="`${subBranch.id}-${automation.event_name}`"
          :branch="subBranch"
          :depth="depth + 1"
          :filter-attributes="filterAttributes"
          :action-types="actionTypes"
          :get-condition-dropdown-values="getConditionDropdownValues"
          :get-action-dropdown-values="getActionDropdownValues"
          :all-custom-attributes="allCustomAttributes"
          :automation="automation"
          :automation-types="automationTypes"
          @update="updateSubBranch(subIndex, $event)"
          @remove="removeSubBranch(subIndex)"
        />
      </div>
    </section>

    <!-- Botón Agregar Sub-Rama -->
    <div class="mt-4 border-t border-slate-200 pt-4 dark:border-slate-700">
      <NextButton
        icon="i-lucide-git-branch"
        slate
        faded
        sm
        :label="$t('AUTOMATION.BRANCHES.ADD_SUB_BRANCH')"
        @click="addSubBranch"
      />
    </div>
  </div>
</template>
